import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { getDealSalesAmount } from '@/utils/salesAmount';

export interface ConversionDetail {
  deal_id: string;
  referred_contact_id: string;
  referred_name: string;
  conversion_type: 'direct' | 'indirect';
  deal_source: string;
  is_reactivation: boolean;
  deal_amount: number;
  deal_stage: string;
  closed_at: string | null;
}

export interface ReferrerStats {
  referrer_contact_id: string;
  referrer_name: string;
  total_contact_referrals: number;
  total_deal_referrals: number;
  total_referrals: number;
  converted_to_customer: number;
  pending_conversion: number;
  conversion_rate: number;
  total_deal_value: number;
  total_revenue_received: number;
  first_referral_at: string | null;
  direct_conversions: number;
  indirect_conversions: number;
  direct_deal_value: number;
  indirect_deal_value: number;
  conversion_details: ConversionDetail[];
}

export interface PendingReferral {
  id: string;
  referred_contact_id: string;
  referred_name: string;
  referrer_contact_id: string;
  referrer_name: string;
  referral_date: string;
  has_deal: boolean;
  last_contact_date: string | null;
}

export interface ScatterDataPoint {
  referrer_contact_id: string;
  referrer_name: string;
  referrals: number;
  deal_value: number;
  conversion_rate: number;
}

export interface ReferralsMetricsData {
  totalReferrals: number;
  totalContactReferrals: number;
  totalDealReferrals: number;
  convertedToCustomer: number;
  pendingConversion: number;
  conversionRate: number;
  totalDealValue: number;
  directConversions: number;
  indirectConversions: number;
  directDealValue: number;
  indirectDealValue: number;
  directConversionRate: number;
  topReferrersByVolume: ReferrerStats[];
  topReferrersByValue: ReferrerStats[];
  effortVsResultData: ScatterDataPoint[];
  pendingReferrals: PendingReferral[];
}

interface DateRange {
  from: Date | null;
  to: Date | null;
}

export function useReferralsMetrics(dateRange?: DateRange) {
  return useQuery({
    queryKey: ['referrals-metrics', dateRange?.from?.toISOString(), dateRange?.to?.toISOString()],
    queryFn: async (): Promise<ReferralsMetricsData> => {
      // Fetch all referrals with related data
      let query = supabase
        .from('referrals')
        .select(`
          id,
          referrer_contact_id,
          referred_contact_id,
          referral_type,
          deal_id,
          created_at,
          is_active,
          referrer_contact:contacts!referrer_contact_id(
            id,
            first_name,
            last_name
          ),
          referred_contact:contacts!referred_contact_id(
            id,
            first_name,
            last_name,
            customer_status
          )
        `)
        .eq('is_active', true);

      if (dateRange?.from) {
        query = query.gte('created_at', dateRange.from.toISOString());
      }
      if (dateRange?.to) {
        query = query.lte('created_at', dateRange.to.toISOString());
      }

      const { data: referrals, error } = await query;

      if (error) throw error;

      // Fetch all WON deals to check conversions
      const { data: allDeals, error: dealsError } = await supabase
        .from('deals')
        .select('id, contact_id, deals_source, is_reactivation, total_amount, stage, updated_at, deal_status, closed_date, cancelled_at')
        .eq('stage', 'won');

      if (dealsError) {
        console.warn('Could not fetch deals data:', dealsError);
      }

      const wonDeals = allDeals || [];

      // Also fetch revenue data from view
      const { data: revenueData, error: revenueError } = await supabase
        .from('v_referrer_revenue')
        .select('*');

      if (revenueError) {
        console.warn('Could not fetch revenue data:', revenueError);
      }

      // Process data
      const referrerMap = new Map<string, ReferrerStats>();
      let totalContactReferrals = 0;
      let totalDealReferrals = 0;
      let directConversions = 0;
      let indirectConversions = 0;
      let pendingConversion = 0;
      let totalDealValue = 0;
      let directDealValue = 0;
      let indirectDealValue = 0;
      const pendingReferrals: PendingReferral[] = [];
      
      // Track which deals have been counted globally to avoid duplicates
      const globalCountedDealIds = new Set<string>();
      // Track contacts that have been processed for this referrer
      const processedContactsByReferrer = new Map<string, Set<string>>();
      // Map referred contact to their referrer for lookup
      const referredContactToReferrer = new Map<string, { referrerId: string; referrerName: string }>();

      // First pass: build the referrer map and track referral relationships
      referrals?.forEach((ref: any) => {
        const referrerId = ref.referrer_contact_id;
        const referredContactId = ref.referred_contact_id;
        const referrerName = ref.referrer_contact
          ? `${ref.referrer_contact.first_name || ''} ${ref.referrer_contact.last_name || ''}`.trim()
          : 'Desconhecido';
        const referredName = ref.referred_contact
          ? `${ref.referred_contact.first_name || ''} ${ref.referred_contact.last_name || ''}`.trim()
          : 'Desconhecido';

        // Track referral relationship
        if (!referredContactToReferrer.has(referredContactId)) {
          referredContactToReferrer.set(referredContactId, { referrerId, referrerName });
        }

        // Count by type
        if (ref.referral_type === 'contact') {
          totalContactReferrals++;
        } else if (ref.referral_type === 'deal') {
          totalDealReferrals++;
        }

        // Initialize referrer stats if needed
        if (!referrerMap.has(referrerId)) {
          referrerMap.set(referrerId, {
            referrer_contact_id: referrerId,
            referrer_name: referrerName,
            total_contact_referrals: 0,
            total_deal_referrals: 0,
            total_referrals: 0,
            converted_to_customer: 0,
            pending_conversion: 0,
            conversion_rate: 0,
            total_deal_value: 0,
            total_revenue_received: 0,
            first_referral_at: ref.created_at,
            direct_conversions: 0,
            indirect_conversions: 0,
            direct_deal_value: 0,
            indirect_deal_value: 0,
            conversion_details: [],
          });
          processedContactsByReferrer.set(referrerId, new Set());
        }

        const stats = referrerMap.get(referrerId)!;
        stats.total_referrals++;
        
        if (ref.referral_type === 'contact') {
          stats.total_contact_referrals++;
        } else {
          stats.total_deal_referrals++;
        }

        // Track first referral
        if (ref.created_at < stats.first_referral_at!) {
          stats.first_referral_at = ref.created_at;
        }
      });

      // Second pass: process conversions based on referred contacts' deals
      referrals?.forEach((ref: any) => {
        const referrerId = ref.referrer_contact_id;
        const referredContactId = ref.referred_contact_id;
        const referredName = ref.referred_contact
          ? `${ref.referred_contact.first_name || ''} ${ref.referred_contact.last_name || ''}`.trim()
          : 'Desconhecido';
        const referrerName = ref.referrer_contact
          ? `${ref.referrer_contact.first_name || ''} ${ref.referrer_contact.last_name || ''}`.trim()
          : 'Desconhecido';

        const stats = referrerMap.get(referrerId)!;
        const processedContacts = processedContactsByReferrer.get(referrerId)!;

        // Only process each referred contact once per referrer
        if (processedContacts.has(referredContactId)) return;
        processedContacts.add(referredContactId);

        // Get ALL WON deals for this referred contact
        const contactWonDeals = wonDeals.filter(d => d.contact_id === referredContactId);

        if (contactWonDeals.length === 0) {
          // No WON deals = PENDING
          pendingConversion++;
          stats.pending_conversion++;
          pendingReferrals.push({
            id: ref.id,
            referred_contact_id: referredContactId,
            referred_name: referredName,
            referrer_contact_id: referrerId,
            referrer_name: referrerName,
            referral_date: ref.created_at,
            has_deal: !!ref.deal_id,
            last_contact_date: null,
          });
        } else {
          // Process each WON deal
          contactWonDeals.forEach(deal => {
            // Usar valor efetivo (regra de cancelamento no mesmo mês)
            // amount_received não está disponível nesta query, usar 0 como fallback
            const dealAmount = getDealSalesAmount({
              deal_status: deal.deal_status,
              closed_date: deal.closed_date,
              cancelled_at: deal.cancelled_at,
              total_amount: deal.total_amount,
              amount_received: 0, // Deals WON geralmente não são cancelados, então isso é OK
            });
            const isDirectConversion = 
              deal.deals_source === 'Customer Referral' && 
              deal.is_reactivation !== true;

            const conversionDetail: ConversionDetail = {
              deal_id: deal.id,
              referred_contact_id: referredContactId,
              referred_name: referredName,
              conversion_type: isDirectConversion ? 'direct' : 'indirect',
              deal_source: deal.deals_source || 'Unknown',
              is_reactivation: deal.is_reactivation === true,
              deal_amount: dealAmount,
              deal_stage: deal.stage,
              closed_at: deal.updated_at,
            };

            stats.conversion_details.push(conversionDetail);

            if (isDirectConversion) {
              // DIRECT: source = 'Customer Referral' AND !is_reactivation
              if (!globalCountedDealIds.has(deal.id)) {
                directConversions++;
                directDealValue += dealAmount;
                totalDealValue += dealAmount;
                globalCountedDealIds.add(deal.id);
              }
              stats.direct_conversions++;
              stats.direct_deal_value += dealAmount;
              stats.total_deal_value += dealAmount;
            } else {
              // INDIRECT: any other source OR is_reactivation
              if (!globalCountedDealIds.has(deal.id)) {
                indirectConversions++;
                indirectDealValue += dealAmount;
                totalDealValue += dealAmount;
                globalCountedDealIds.add(deal.id);
              }
              stats.indirect_conversions++;
              stats.indirect_deal_value += dealAmount;
              stats.total_deal_value += dealAmount;
            }
          });
        }
      });

      // Add revenue data from view
      revenueData?.forEach((rev: any) => {
        const stats = referrerMap.get(rev.referrer_contact_id);
        if (stats) {
          stats.total_revenue_received = parseFloat(rev.total_revenue_received) || 0;
        }
      });

      // Calculate conversion rates and converted_to_customer
      referrerMap.forEach((stats) => {
        const totalConverted = stats.direct_conversions + stats.indirect_conversions;
        stats.conversion_rate = stats.total_referrals > 0
          ? (totalConverted / stats.total_referrals) * 100
          : 0;
        stats.converted_to_customer = totalConverted;
      });

      const referrersList = Array.from(referrerMap.values());

      // Sort by volume
      const topReferrersByVolume = [...referrersList]
        .sort((a, b) => b.total_referrals - a.total_referrals)
        .slice(0, 20);

      // Sort by value
      const topReferrersByValue = [...referrersList]
        .sort((a, b) => b.total_deal_value - a.total_deal_value)
        .slice(0, 20);

      // Build scatter data
      const effortVsResultData: ScatterDataPoint[] = referrersList.map((r) => ({
        referrer_contact_id: r.referrer_contact_id,
        referrer_name: r.referrer_name,
        referrals: r.total_referrals,
        deal_value: r.total_deal_value,
        conversion_rate: r.conversion_rate,
      }));

      const totalReferrals = totalContactReferrals + totalDealReferrals;
      const convertedToCustomer = directConversions + indirectConversions;
      const conversionRate = totalReferrals > 0
        ? (convertedToCustomer / totalReferrals) * 100
        : 0;
      const directConversionRate = totalReferrals > 0
        ? (directConversions / totalReferrals) * 100
        : 0;

      return {
        totalReferrals,
        totalContactReferrals,
        totalDealReferrals,
        convertedToCustomer,
        pendingConversion,
        conversionRate,
        totalDealValue,
        directConversions,
        indirectConversions,
        directDealValue,
        indirectDealValue,
        directConversionRate,
        topReferrersByVolume,
        topReferrersByValue,
        effortVsResultData,
        pendingReferrals: pendingReferrals.slice(0, 50),
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
